import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import { Form, Formik } from 'formik';

import { Input, Modal, PaginationArrow, SingleSelect } from '@/common';
import { UploadButton } from '@/components/common';
import { Label, LABEL_TYPE } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { queryClient } from '@/configuration/react-query';
import { IMAGE, PAGE_SIZE, UPLOAD_PICKER_TYPES, VIDEO } from '@/constants';
import { AssetScope, AssetType, COLORS } from '@/enums';
import { ClipType } from '@/enums/VideoThemeClipEnum';
import { showToast, ToastType } from '@/helpers/toast';
import { useFileUpload } from '@/hooks';
import { AssetItem } from '@/pages/Event/Asset/AssetItem';
import { createEditClipValidation } from '@/pages/VideoTheme/Clips/Validation';
import { PublishIcons } from '@/resources';
import { useGetPublicAssets } from '@/services';
import { useGetStockClips } from '@/services/stockClip.service';
import {
  useCreateVideoThemeClipMutation,
  useUpdateVideoThemeClip
} from '@/services/videoThemeClips.service';
import { Asset, SelectOptionProps } from '@/types';
import { StockClipAsset } from '@/types/StockClipTypes';
import { CreateEditVideoThemeClip, VideoThemeClip } from '@/types/VideoThemeTypes';

type CreateEditClipProps = {
  isOpen: boolean;
  isCreate: boolean;
  videoThemeId?: number;
  clip?: VideoThemeClip;
  onClose: () => void;
};

export const CreateEditClip = ({
  isOpen,
  onClose,
  isCreate,
  videoThemeId,
  clip
}: CreateEditClipProps) => {
  const [t] = useTranslation('common');
  const [selectedAsset, setSelectedAsset] = useState<Asset>();
  const { ref, inView } = useInView();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showRecommendedVideos, setShowRecommendedVideos] = useState(false);

  useEffect(() => {
    if (clip && !isCreate) {
      setSelectedAsset(clip.asset);
    }
  }, [clip, isCreate]);

  const getClipTypeOptions = () => {
    const keys: string[] = Object.keys(ClipType).filter((key) => isNaN(Number(key)));

    return keys.map((key: string) => {
      return {
        value: ClipType[key as keyof typeof ClipType],
        label: key
      };
    });
  };
  const { data: recommendedStockClipsData } = useGetStockClips(
    PAGE_SIZE,
    undefined,
    undefined,
    videoThemeId,
    false,
    'StockClipName'
  );

  const {
    isLoading: isLoadingStockClips,
    data: stockClipsData,
    fetchNextPage: stockClipsFetchNextPage,
    isFetchingNextPage: isStockClipsFetchingNextPage,
    hasNextPage: stockClipsHasNextPage
  } = useGetStockClips(PAGE_SIZE, undefined, undefined, videoThemeId, true, 'StockClipName');

  const {
    isLoading: isLoading,
    data: publicAssetsData,
    fetchNextPage: publicAssetsFetchNextPage,
    isFetchingNextPage: isPublicAssetsFetchingNextPage,
    hasNextPage: publicAssetsHasNextPage,
    refetch: publicAssetsRefetch
  } = useGetPublicAssets([''], PAGE_SIZE, undefined, '', [VIDEO, IMAGE], 'Id', false);

  const { uploadFiles, isUploading } = useFileUpload({
    pickerTypes: UPLOAD_PICKER_TYPES.IMAGE_VIDEO,
    isPublic: true,
    assetScope: AssetScope.PUBLIC,
    onCreatedAsset: () => {
      publicAssetsRefetch();
    }
  });

  useEffect(() => {
    if (inView) {
      publicAssetsHasNextPage && publicAssetsFetchNextPage();
      stockClipsHasNextPage && stockClipsFetchNextPage();
    }

    const handleMouseDown = (event: MouseEvent) => {
      if (event.shiftKey) {
        event.preventDefault();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [inView]);

  const handleClickAsset = (asset: Asset, event: React.MouseEvent) => {
    event.preventDefault();
    setSelectedAsset(asset);
  };

  const { mutate: createVideoThemeClip } = useCreateVideoThemeClipMutation(queryClient, {
    onSuccess: () => {
      onClose();
      showToast(ToastType.SUCCESS, t('videoThemeClipCreated'));
      setIsSubmitting(false);
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
      setIsSubmitting(false);
    }
  });

  const { mutate: updateVideoThemeClip } = useUpdateVideoThemeClip(queryClient, {
    onSuccess: () => {
      onClose();
      showToast(ToastType.SUCCESS, t('videoThemeClipSuccessfullyUpdated'));
      setIsSubmitting(false);
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
      setIsSubmitting(false);
    }
  });

  const getInitialValues = () => {
    const initialValues: CreateEditVideoThemeClip & { assetType?: string; duration?: number } = {
      id: clip ? clip.id : undefined,
      videoThemeId: videoThemeId,
      type: clip ? clip.type : '',
      assetId: selectedAsset?.id || 0,
      order: clip ? clip.order : undefined,
      assetType: clip?.asset.assetType || '',
      duration: clip?.duration || clip?.asset?.duration || 0
    };

    return initialValues;
  };

  const handleSubmit = async (
    values: CreateEditVideoThemeClip & { assetType?: string; duration?: number }
  ) => {
    if (!selectedAsset) {
      showToast(ToastType.ERROR, t('youMustSelectClip'));
      return;
    }
    values.assetId = selectedAsset.id;
    setIsSubmitting(true);
    const payload: CreateEditVideoThemeClip = {
      id: values.id,
      videoThemeId: values.videoThemeId,
      type: values.type,
      assetId: values.assetId,
      order: values.order,
      duration: values.duration
    };

    if (isCreate) {
      await createVideoThemeClip(payload);
    } else {
      await updateVideoThemeClip(payload);
    }
  };

  const getTypeDefaultValue = (type: string) => {
    return type ? (getClipTypeOptions().find((option) => option.label === type) ?? null) : null;
  };

  const publicAssets = publicAssetsData?.pages.flatMap((page) => page?.data?.items) || [];
  const stockClips =
    stockClipsData?.pages.flatMap((page) =>
      page?.data?.items?.map((asset: StockClipAsset) => asset.stockClipAsset)
    ) || [];

  const combinedAssets = [...publicAssets, ...stockClips].sort((a, b) => b.id - a.id);
  const recommended = recommendedStockClipsData?.pages?.flatMap((page) =>
    page?.data?.items?.map((asset: StockClipAsset) => asset.stockClipAsset)
  );

  return (
    <Formik
      initialValues={getInitialValues()}
      onSubmit={handleSubmit}
      validationSchema={createEditClipValidation}>
      {({ errors, touched, values, handleSubmit, setFieldValue, dirty, setFieldTouched }) => (
        <Modal
          isOpen={isOpen}
          height='h-full'
          label={isCreate ? t('addNewClip') : t('editClip')}
          onClose={onClose}
          modalClasses='md:w-[800px] border border-secondary-blue-gray'
          containerClasses='no-scrollbar'
          hasButton
          hasCancelButton
          buttonText={isCreate ? t('addClip') : t('editClip')}
          buttonClasses='flex justify-end'
          buttonType={ButtonVariant.PRIMARY}
          isButtonDisabled={
            isSubmitting ||
            (!isCreate && !dirty) ||
            Object.keys(errors).length > 0 ||
            selectedAsset === undefined
          }
          handleSubmit={handleSubmit}>
          <Form>
            <div className='w-full border border-solid border-secondary-blue-gray'></div>
            <div className='mb-4 mt-6 flex w-full flex-row justify-center gap-x-4'>
              <div className='w-1/2'>
                <SingleSelect
                  className='w-full'
                  label={t('type')}
                  isClearable
                  placeholder={t('type')}
                  id='type'
                  isRequired
                  value={getTypeDefaultValue(values.type)}
                  options={getClipTypeOptions()}
                  handleChange={(value) => {
                    setFieldValue('type', (value as SelectOptionProps)?.label);
                  }}
                  errorMessage={errors.type}
                  isInvalid={!!errors.type && touched.type}
                  onBlur={() => setFieldTouched('type', true)}
                />
              </div>
              <div className='w-1/2'>
                <Input
                  className='w-full'
                  isInvalid={!!errors.order && touched.order}
                  errorMessage={touched.order ? errors.order : undefined}
                  label={t('order')}
                  id='order'
                  isRequired
                  type={'number'}
                />
              </div>
            </div>
            {selectedAsset?.assetType.toUpperCase() === AssetType[AssetType.IMAGE] && (
              <div className='mb-2 flex w-full flex-col gap-y-2'>
                <Label
                  className='text-secondary-gray'
                  text={`${t('selectAsset')}:`}
                  type={LABEL_TYPE.PARAGRAPH1}
                />
                <div className='flex w-full flex-row justify-center gap-x-4'>
                  <div className='w-1/2'>
                    <Input
                      className='w-full'
                      id='assetType'
                      label={t('assetType')}
                      readOnly
                      disabled
                      type={'text'}
                    />
                  </div>
                  <div className='w-1/2'>
                    <Input
                      className='w-full'
                      id='duration'
                      label={t('durationInSeconds')}
                      isInvalid={!!errors.duration && touched.duration}
                      errorMessage={touched.duration ? errors.duration : undefined}
                      isRequired
                      type={'number'}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className='flex w-full grow flex-col justify-between'>
              <UploadButton
                className='mx-[5px] mb-4 mt-2 w-[calc(100%-10px)]'
                multiple
                text='uploadNewVideoOrImage'
                pickerTypes={UPLOAD_PICKER_TYPES.IMAGE_VIDEO}
                showMaxFileSize
                variant={ButtonVariant.QUINARY}
                isUploading={isUploading}
                disabled={isUploading}
                icon={<PublishIcons fill={COLORS.PRIMARY_BLUE} className='mr-1 size-4' />}
                onFilesSelected={uploadFiles}
              />
              <div className='w-full text-center text-base font-normal text-secondary-gray'>
                {t('orSelectFromOneOfTheExistingItem')}
              </div>
            </div>
            {recommendedStockClipsData?.pages?.some((page) => page.data?.items.length > 0) && (
              <div className='mt-4 flex w-full flex-row'>
                <div
                  className={`flex w-1/2 cursor-pointer flex-row items-center justify-center border-b-2 pb-2 ${showRecommendedVideos ? 'border-primary-blue font-semibold text-primary-blue' : 'text-secondary-gray'}`}
                  onClick={() => setShowRecommendedVideos(true)}>
                  {t('recommendedVideos')}
                </div>
                <div
                  className={`flex w-1/2 cursor-pointer flex-row items-center justify-center border-b-2 pb-2 ${showRecommendedVideos ? 'text-secondary-gray' : 'border-primary-blue font-semibold text-primary-blue'}`}
                  onClick={() => setShowRecommendedVideos(false)}>
                  {t('allExistingVideos')}
                </div>
              </div>
            )}
            <div className='mt-4'>
              <div className='grid grid-cols-3 items-center justify-between gap-2 pr-2 md:grid-cols-5 md:gap-4 md:pt-4'>
                {!isLoading && !isLoadingStockClips && (
                  <>
                    {(showRecommendedVideos ? recommended : combinedAssets)?.map((asset: Asset) => (
                      <div onClick={(e) => handleClickAsset(asset, e)} key={asset.id}>
                        <AssetItem
                          setSelectedAsset={() => {
                            setSelectedAsset;
                            setFieldValue('assetType', asset.assetType);
                            setFieldValue('duration', asset.duration ? asset.duration : 0);
                          }}
                          asset={asset}
                          key={asset.id}
                          selectedAsset={selectedAsset as Asset}
                          canBeDeleted={false}
                          canBeEdited={false}
                          multiselect={false}
                          selectedVideoUrl={''}
                          clipSelect={true}
                        />
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
            <div ref={ref} className='h-1 w-full'></div>
            {!isLoading && (
              <PaginationArrow
                isFetchingNextPage={isPublicAssetsFetchingNextPage || isStockClipsFetchingNextPage}
                inView={inView}
              />
            )}
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
